import { useId, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Pencil,
  Settings,
  Shield,
  ShieldCheck,
  Wrench,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"

import { AdminTableToolbar } from "./admin-table-toolbar"
import { type NormalizedToolAction, normalizeToolActions } from "./tools-table-utils"
import { useAdminTableControls } from "../hooks/use-admin-table-controls"

type ToolAction = NormalizedToolAction

export interface Tool {
  name: string
  description: string
  tags: string[]
  version: string
  enabled: boolean
  access_configured?: boolean
  hasActions?: boolean
}

interface ToolRowProps {
  tool: Tool
  isUpdating: boolean
  disableAll: boolean
  onToggleStatus: (enabled: boolean) => void
  onEditToolAccess?: () => void
  onEditToolConfig?: () => void
  hasConfigurableSettings?: boolean
  /** Opens the Form + Raw edit dialog for this tool. */
  onEditTool?: () => void
  actions?: ToolAction[]
  isLoadingActions: boolean
  onToggleAction?: (actionName: string, enabled: boolean) => void
  onToggleAllActions?: (enabled: boolean) => void
  onEditActionAccess?: (actionName: string) => void
  isUpdatingAction?: string
  agentName: string
  onExpandActions?: () => void
}

function ToolRow({
  tool,
  isUpdating,
  disableAll,
  onToggleStatus,
  onEditToolAccess,
  onEditToolConfig,
  hasConfigurableSettings,
  onEditTool,
  actions,
  isLoadingActions,
  onToggleAction,
  onToggleAllActions,
  onEditActionAccess,
  isUpdatingAction,
  onExpandActions,
}: ToolRowProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const descriptionId = useId()
  const hasLongDescription = tool.description.length > 160

  const handleExpand = () => {
    if (!isExpanded && onExpandActions) {
      onExpandActions()
    }
    setIsExpanded(!isExpanded)
  }

  const enabledActionsCount = actions?.filter((a) => a.enabled).length ?? 0
  const totalActionsCount = actions?.length ?? 0
  const actionsRegionId = `tool-actions-${tool.name.replaceAll(/[^a-zA-Z0-9_-]/g, "-")}`

  const renderActionsContent = () => {
    if (isLoadingActions) {
      return (
        <div className="text-muted-foreground flex items-center justify-center py-4 text-sm">
          {t("adminDashboard.tools.loadingActions", "Loading actions...")}
        </div>
      )
    }

    if (actions && actions.length > 0) {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between pb-2">
            <span className="text-muted-foreground text-xs font-medium">
              {t("adminDashboard.tools.actions", "Actions")}
            </span>
            {onToggleAllActions && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-xs">
                  {t("adminDashboard.tools.enableAllActions", "Enable All")}
                </span>
                <Switch
                  aria-label={t("adminDashboard.tools.enableAllActions", "Enable All")}
                  checked={enabledActionsCount === totalActionsCount}
                  disabled={disableAll || !tool.enabled || isUpdatingAction !== undefined}
                  onCheckedChange={onToggleAllActions}
                />
              </div>
            )}
          </div>
          {actions.map((action) => (
            <div
              key={action.name}
              className="bg-background/50 flex items-center justify-between rounded-md px-3 py-2"
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">{action.name}</span>
                <span className="text-muted-foreground text-xs">{action.description || "—"}</span>
              </div>
              <div className="flex items-center">
                <Button
                  aria-label={`${t("adminDashboard.access.access", { defaultValue: "Access" })} ${tool.name} ${action.name}`}
                  className="mr-2"
                  disabled={disableAll || !onEditActionAccess}
                  size="icon"
                  type="button"
                  variant="outline"
                  onClick={() => onEditActionAccess?.(action.name)}
                >
                  {action.access_configured ? (
                    <ShieldCheck className="text-success size-4" />
                  ) : (
                    <Shield className="size-4" />
                  )}
                </Button>
                <Switch
                  aria-label={`${action.name} status`}
                  checked={action.enabled}
                  disabled={disableAll || !tool.enabled || isUpdatingAction === action.name}
                  onCheckedChange={(checked) => onToggleAction?.(action.name, checked)}
                />
                <span className="text-muted-foreground ml-2 w-16 text-xs">
                  {action.enabled
                    ? t("adminDashboard.tools.enabled", "Enabled")
                    : t("adminDashboard.tools.disabled", "Disabled")}
                </span>
              </div>
            </div>
          ))}
        </div>
      )
    }

    return (
      <div className="text-muted-foreground py-2 text-sm">
        {t("adminDashboard.tools.noActions", "No configurable actions")}
      </div>
    )
  }

  return (
    <div className="bg-card rounded-lg border text-sm transition-colors">
      <div
        className={cn(
          "admin-responsive-grid-row grid grid-cols-12 gap-4 p-4",
          tool.hasActions && "hover:bg-accent/50",
          tool.hasActions && (isExpanded ? "rounded-t-lg" : "rounded-lg")
        )}
      >
        <div className="col-span-3">
          <button
            aria-controls={tool.hasActions ? actionsRegionId : undefined}
            aria-expanded={tool.hasActions ? isExpanded : undefined}
            disabled={!tool.hasActions}
            type="button"
            className={cn(
              "flex min-h-11 w-full items-center gap-2 rounded-md text-left focus-visible:ring-2 focus-visible:outline-none",
              !tool.hasActions && "cursor-default"
            )}
            onClick={handleExpand}
          >
            {tool.hasActions ? (
              <span className="text-muted-foreground shrink-0">
                {isExpanded ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronRight className="size-4" />
                )}
              </span>
            ) : null}
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{tool.name}</span>
              <span className="text-muted-foreground text-xs">v{tool.version}</span>
              {tool.hasActions && actions ? (
                <span className="text-muted-foreground text-xs">
                  {t("adminDashboard.tools.actionsCount", "{{enabled}}/{{total}} actions", {
                    enabled: enabledActionsCount,
                    total: totalActionsCount,
                  })}
                </span>
              ) : null}
            </span>
          </button>
        </div>
        <div className="col-span-7 min-w-0">
          <span className="text-foreground mb-1 block text-xs font-medium lg:hidden">
            {t("adminDashboard.tools.description", "Description")}
          </span>
          <p
            id={descriptionId}
            className={cn(
              "text-muted-foreground",
              hasLongDescription && !descriptionExpanded && "line-clamp-3"
            )}
          >
            {tool.description || "—"}
          </p>
          {hasLongDescription ? (
            <Button
              aria-controls={descriptionId}
              aria-expanded={descriptionExpanded}
              className="mt-1 h-auto min-h-10 px-0 text-sm"
              type="button"
              variant="link"
              onClick={() => setDescriptionExpanded((value) => !value)}
            >
              {descriptionExpanded
                ? t("adminDashboard.tools.collapseDescription", "Show less")
                : t("adminDashboard.tools.expandDescription", "Read full description")}
            </Button>
          ) : null}
        </div>
        <div className="col-span-2 flex flex-wrap items-center justify-end self-start">
          <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
            Actions & status
          </span>
          {hasConfigurableSettings && (
            <Button
              aria-label={`${t("adminDashboard.tools.config.open", { defaultValue: "Configure tool" })} ${tool.name}`}
              className="mr-2"
              disabled={disableAll || !onEditToolConfig}
              size="icon"
              type="button"
              variant="outline"
              onClick={() => onEditToolConfig?.()}
            >
              <Settings className="size-4" />
            </Button>
          )}
          {onEditTool ? (
            <Button
              aria-label={`${t("adminDashboard.tools.edit", { defaultValue: "Edit tool" })} ${tool.name}`}
              className="mr-1"
              disabled={disableAll}
              size="icon"
              type="button"
              variant="ghost"
              onClick={() => onEditTool()}
            >
              <Pencil className="size-4" />
            </Button>
          ) : null}
          <Button
            aria-label={`${t("adminDashboard.access.access", { defaultValue: "Access" })} ${tool.name}`}
            className="mr-2"
            disabled={disableAll || !onEditToolAccess}
            size="icon"
            type="button"
            variant="outline"
            onClick={() => onEditToolAccess?.()}
          >
            {tool.access_configured ? (
              <ShieldCheck className="text-success size-4" />
            ) : (
              <Shield className="size-4" />
            )}
          </Button>
          <Switch
            aria-label={`${tool.name} status`}
            checked={tool.enabled}
            disabled={disableAll || isUpdating}
            onCheckedChange={onToggleStatus}
          />
          <span className="text-muted-foreground ml-2 text-xs">
            {tool.enabled
              ? t("adminDashboard.tools.enabled", "Enabled")
              : t("adminDashboard.tools.disabled", "Disabled")}
          </span>
        </div>
      </div>

      {/* Expandable actions section */}
      {tool.hasActions && isExpanded && (
        <div className="bg-muted/30 rounded-b-lg border-t px-4 py-3" id={actionsRegionId}>
          {renderActionsContent()}
        </div>
      )}
    </div>
  )
}

const TOOLS_WITH_CONFIG = new Set(["web_search"])

export function ToolsTable({
  tools,
  onToggleStatus,
  isUpdating,
  disableAll,
  agentName,
  toolActions,
  loadingActionsFor,
  onLoadActions,
  onToggleAction,
  onToggleAllActions,
  onEditToolAccess,
  onEditToolConfig,
  onEditTool,
  onEditActionAccess,
  isUpdatingAction,
}: {
  tools: Tool[]
  onToggleStatus: (toolName: string, enabled: boolean) => void
  isUpdating?: string
  disableAll?: boolean
  agentName: string
  toolActions?: Record<string, unknown>
  loadingActionsFor?: string
  onLoadActions?: (toolName: string) => void
  onToggleAction?: (toolName: string, actionName: string, enabled: boolean) => void
  onToggleAllActions?: (toolName: string, enabled: boolean) => void
  onEditToolAccess?: (toolName: string) => void
  onEditToolConfig?: (toolName: string) => void
  /** Opens the Form + Raw edit dialog for this tool. */
  onEditTool?: (toolName: string) => void
  onEditActionAccess?: (toolName: string, actionName: string) => void
  isUpdatingAction?: string
}) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  // Check if tool has actions by looking at the toolActions data or schema
  const toolsWithActionsInfo = tools.map((tool) => ({
    ...tool,
    hasActions: toolActions?.[tool.name] !== undefined || tool.name === "microsoft_365_search",
  }))

  const controls = useAdminTableControls(toolsWithActionsInfo, {
    getName: (row) => row.name,
    getSearchText: (row) => [row.name, row.description, ...row.tags].join(" "),
    isEnabled: (row) => row.enabled,
  })
  const displayTools = isExpanded ? controls.rows : controls.rows.slice(0, 5)
  const hasMoreTools = controls.count > 5

  if (tools.length === 0) {
    return (
      <EmptyState
        description={t("adminDashboard.tools.emptyDescription", "No tools available")}
        icon={Wrench}
        title={t("adminDashboard.tools.emptyTitle", "No Tools")}
      />
    )
  }

  return (
    <div className="space-y-3">
      <AdminTableToolbar bulkActions controls={controls} />
      <div className="text-muted-foreground hidden grid-cols-12 gap-4 px-4 py-2 text-sm font-medium lg:grid">
        <div className="col-span-3">{t("adminDashboard.tools.name", "Tool Name")}</div>
        <div className="col-span-7">{t("adminDashboard.tools.description", "Description")}</div>
        <div className="col-span-2 text-right">{t("adminDashboard.tools.status", "Status")}</div>
      </div>

      {displayTools.map((tool) => (
        <ToolRow
          key={tool.name}
          actions={normalizeToolActions(toolActions?.[tool.name])}
          agentName={agentName}
          disableAll={disableAll ?? false}
          hasConfigurableSettings={TOOLS_WITH_CONFIG.has(tool.name)}
          isLoadingActions={loadingActionsFor === tool.name}
          isUpdating={isUpdating === tool.name}
          isUpdatingAction={isUpdatingAction}
          tool={tool}
          onEditTool={onEditTool ? () => onEditTool(tool.name) : undefined}
          onEditToolAccess={onEditToolAccess ? () => onEditToolAccess(tool.name) : undefined}
          onExpandActions={() => onLoadActions?.(tool.name)}
          onToggleStatus={(enabled) => onToggleStatus(tool.name, enabled)}
          onEditActionAccess={
            onEditActionAccess
              ? (actionName) => onEditActionAccess(tool.name, actionName)
              : undefined
          }
          onEditToolConfig={
            onEditToolConfig && TOOLS_WITH_CONFIG.has(tool.name)
              ? () => onEditToolConfig(tool.name)
              : undefined
          }
          onToggleAction={
            onToggleAction
              ? (actionName, enabled) => onToggleAction(tool.name, actionName, enabled)
              : undefined
          }
          onToggleAllActions={
            onToggleAllActions ? (enabled) => onToggleAllActions(tool.name, enabled) : undefined
          }
        />
      ))}

      {hasMoreTools && (
        <div className="flex justify-center pt-2">
          <Button className="w-full" variant="outline" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? (
              <>
                <ChevronUp className="mr-2 size-4" />
                {t("adminDashboard.tools.showLess", "Show Less")}
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 size-4" />
                {t("adminDashboard.tools.showMore", "Show More", {
                  count: controls.count - 5,
                })}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
